<template>
  <NuxtLayout>
    <NuxtPage v-if="!teleportTarget" />

    <template v-else>
      <div class="flex items-end justify-end pr-2">
        <button type="button" @click="modalClose()">
          <span class="sr-only">Close</span>
          <XMarkIcon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <div :id="teleportTarget" class="h-full"></div>
    </template>
  </NuxtLayout>

  <Toasts group="error" position="tr" />

  <Teleport v-if="modal" :to="teleportTo">
    <component v-if="modalExtra?.componentIsModal" :is="modal.component" v-bind="modal.componentProps" />
    <Modal v-else :opt="modal" @close="modalClose"
      :class="modalExtra?.maximize ? !!teleportTarget ? 'min-w-[90vw] min-h-[90vh]' : 'min-w-[70vw] min-h-[70vh]' : undefined" />
  </Teleport>

  <TransitionRoot as="div" :show="modalShow">
    <Dialog as="div" class="fixed z-50 inset-0 overflow-y-auto max-w-screen" @close="modalClose"
      :initialFocus="initialFocus">
      <div class="flex items-center justify-center min-h-screen p-0 sm:pt-4 sm:px-4 sm:pb-20 text-center">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
          leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <TransitionChild as="template" enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div class="modal-wrapper">
            <div class="modal-buttons">
              <button @click="modalInPage" v-if="modal?.gButtonFullscreen">
                <span class="sr-only">Fullscreen</span>
                <ArrowsPointingOutIcon class="w-4" />
              </button>
              <button type="button" @click="modalClose()" v-if="modalFullscreen || !(modal?.gButtonClose === false)">
                <span class="sr-only">Close</span>
                <XMarkIcon class="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div id="modal-content" ref="initialFocus" :class="modalFullscreen ? 'h-screen w-screen' : 'h-full w-full'">
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script lang="ts">
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon, ArrowsPointingOutIcon } from '@heroicons/vue/24/outline'
import { useEventListener } from '@vueuse/core'

import { rootEmitter } from './plugins/03.emitter';

import type { ModalBuitIn, ModalComponent } from './components/Modal.vue';

export type Extra = { onClose: (v: any) => void; maximize?: true; componentIsModal?: true }

export type EventModalBuitIn = ModalBuitIn
export type EventModalComponent = ModalComponent

type EventModal = EventModalBuitIn | EventModalComponent

export default defineComponent({
  components: {
    Dialog, DialogOverlay, TransitionChild, TransitionRoot,
    ArrowsPointingOutIcon, XMarkIcon
  },
  setup() {
    const { $router, $store } = useNuxtApp()

    const modal = ref<EventModal>()
    const modalExtra = ref<Extra>()

    const modalShow = ref(false)
    const teleportTo = ref('#modal-content')
    const teleportTarget = ref<string>()

    let unregisterRouterAfterEach: () => void

    function modalClose(v?: any) {
      if (unregisterRouterAfterEach) unregisterRouterAfterEach()
      if (modalExtra.value.onClose) modalExtra.value.onClose(v)
      modalShow.value = false
      modal.value = undefined
      modalExtra.value = undefined
      teleportTo.value = "#modal-content"
      teleportTarget.value = undefined
    }

    function modalOpen(opt: EventModal, extra: Extra) {
      modalExtra.value = extra
      teleportTo.value = "#modal-content";
      teleportTarget.value = undefined
      modalShow.value = true
      nextTick(() => modal.value = opt)
      unregisterRouterAfterEach = $router.afterEach(() => modalClose())
    }

    function modalInPage() {
      teleportTarget.value = "modal-page"
      nextTick(() => {
        teleportTo.value = "#modal-page"; modalShow.value = false
      })
    }

    onMounted(() => {
      rootEmitter.on('modal', modalOpen)
      rootEmitter.on('modal-close', modalClose)
    })

    function shortcutUser(evt: KeyboardEvent) {
      if (evt.ctrlKey && evt.key === ' ' && evt.code === 'Space')
        rootEmitter.modalContactSearch()
    }

    let cleanEventListener: () => void
    watch(
      () => $store.account?.rights?.isUser,
      isUser => {
        if (isUser)
          cleanEventListener = useEventListener(window.document, 'keydown', shortcutUser)
        else if (cleanEventListener)
          cleanEventListener()
      },
      { immediate: true }
    )

    return {
      modal,
      modalExtra,

      modalShow,
      modalFullscreen: computed(() => $store.display.breakpoint.sm),
      teleportTo,
      teleportTarget,

      modalClose,
      modalInPage,

      initialFocus: ref()
    }
  }
})
</script>

<style lang="postcss">
@import '@tolemac/web-components/style.css';

.modal-wrapper {
  @apply inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle;

  .modal-buttons {
    @apply flex gap-x-4 absolute top-2 right-2;

    >button {
      @apply inline-block bg-white rounded-md text-gray-400 hover:text-gray-500;
    }
  }
}

section {

  &.account-section,
  &.password-section {
    @apply bg-white rounded-xl shadow border-t mb-6 pt-4 my-4;

    /* content */
    >div {
      @apply p-5;

      /* title */
      >h2 {
        @apply text-lg font-medium;
      }

      /* subtitle */
      >p {
        @apply text-gray-500 pb-5;
      }
    }

    /* buttons */
    >.section-footer {
      @apply p-2 bg-gray-50 rounded-b-xl flex flex-row-reverse flex-grow-0;
    }
  }
}
</style>